import React, { useState } from "react";
import { Button } from "../../../design/button";
import { Checkbox, CheckboxField } from "../../../design/checkbox";
import { Label } from "../../../design/fieldset";
import { httpsCallable } from "firebase/functions";
import { doc, updateDoc } from "firebase/firestore";
import { functions, db } from "../../../firebase";
import { ArrowPathIcon } from "@heroicons/react/16/solid";

interface RegenerateReportPDFButtonProps {
  reportId: string;
  hasExistingPDF: boolean;
  /** Current value of the report's includeEvents flag. */
  includeEvents?: boolean;
  /** Whether this report has a visit (and thus events to include). */
  hasVisit?: boolean;
  onSuccess?: (storagePath: string) => void;
}

interface RegenerateReportPDFResponse {
  success: boolean;
  storagePath: string;
  message: string;
}

const RegenerateReportPDFButton: React.FC<RegenerateReportPDFButtonProps> = ({
  reportId,
  hasExistingPDF,
  includeEvents: initialIncludeEvents = false,
  hasVisit = true,
  onSuccess,
}) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [includeEvents, setIncludeEvents] = useState<boolean>(initialIncludeEvents);

  const handleToggleEvents = async (checked: boolean) => {
    setIncludeEvents(checked);
    try {
      await updateDoc(doc(db, "reports", reportId), { includeEvents: checked });
    } catch (err) {
      console.error("Failed to save includeEvents flag:", err);
    }
  };

  const handleGenerate = async () => {
    const confirmMessage = hasExistingPDF
      ? "Are you sure you want to regenerate the PDF? This will replace the existing report PDF."
      : "Generate a PDF for this report?";

    // eslint-disable-next-line no-restricted-globals
    if (!window.confirm(confirmMessage)) {
      return;
    }

    setIsGenerating(true);
    try {
      // Make sure the flag is persisted before the server regenerates.
      await updateDoc(doc(db, "reports", reportId), { includeEvents });
      const result = await httpsCallable<{ reportId: string }, RegenerateReportPDFResponse>(
        functions,
        "regenerateReportPDF"
      )({ reportId });

      if (result.data.success) {
        window.alert(hasExistingPDF ? "PDF regenerated successfully!" : "PDF generated successfully!");
        onSuccess?.(result.data.storagePath);
      }
    } catch (error: unknown) {
      console.error("Error generating report PDF:", error);
      const message = error instanceof Error ? error.message : "Unknown error occurred";
      window.alert(`Failed to generate PDF: ${message}`);
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="flex flex-col items-end gap-2">
      {hasVisit && (
        <CheckboxField>
          <Checkbox
            name="includeEvents"
            checked={includeEvents}
            onChange={(checked) => handleToggleEvents(checked)}
          />
          <Label className="text-sm">Attach visit events to the report</Label>
        </CheckboxField>
      )}
      <Button
        outline
        onClick={handleGenerate}
        disabled={isGenerating}
        className="flex items-center gap-2"
      >
        <ArrowPathIcon className={`w-4 h-4 ${isGenerating ? "animate-spin" : ""}`} />
        {isGenerating
          ? "Generating..."
          : hasExistingPDF
            ? "Regenerate PDF"
            : "Generate PDF"}
      </Button>
    </div>
  );
};

export default RegenerateReportPDFButton;
